import { useUser } from "@clerk/clerk-react";
import { AlertCircle, Calendar, CheckCircle, Loader2, Users } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { WelcomeBanner } from "@/components/dashboard/WelcomeBanner";
import { discountLabel, useFreemiumDiscount } from "@/components/freemium/UnlockScreen";
import { TestCardInfo } from "@/components/TestCardInfo";
import { useEnrollment } from "@/contexts/EnrollmentContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import { MinimalLayout } from "@/layouts/MinimalLayout";
import { api } from "@/services/api";
import type { Programme, UserProfile } from "@/types";
import { formatCurrency } from "@/utils/payment";

const NAME_FIELD_CLASS =
  "w-full border border-content-border bg-white px-3 py-2 text-sm text-content-foreground outline-none focus:border-brand/60";

// "What to Expect" tiles as [value, label].
const PAID_EXPECTATIONS = [
  ["4 Weeks", "Typical Duration"],
  ["8 Reports", "Total Work Packages"],
  ["~10 hrs", "Weekly Commitment"],
  ["70%", "Pass Score"],
] as const;

const TASTER_EXPECTATIONS = [
  ["1 Week", "Taster Duration"],
  ["2 Work Packages", "To Complete"],
  ["2 Reviews", "Feedback on both"],
  ["Free", "No card needed"],
] as const;

/** A freemium cohort's participation badge carries the Participant's name, so
 * the API refuses the join until both names are set (KC-495). */
function needsNames(programme: Programme, profile: UserProfile): boolean {
  return (
    Boolean(programme.is_freemium) && !(profile.first_name?.trim() && profile.last_name?.trim())
  );
}

export default function ProgramRegistrationPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refetch: refetchEnrollment } = useEnrollment();
  const {
    userProfile,
    isLoading: isLoadingProfile,
    error: profileError,
    refetch: refetchProfile,
  } = useUserProfile();
  const { user: clerkUser } = useUser();
  const [namePromptFor, setNamePromptFor] = useState<string | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isJoining, setIsJoining] = useState<string | null>(null);
  const [isLoadingProgrammes, setIsLoadingProgrammes] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const autoJoinAttempted = useRef(false);
  // The ?programme_id the page was opened with — the param itself is cleared
  // once auto-join fires, but the wording below still follows that Cohort.
  const linkedId = useRef(searchParams.get("programme_id"));
  // Free Participants get an automatic discount at checkout (KC-498)
  const offer = discountLabel(useFreemiumDiscount());

  useEffect(() => {
    const fetchProgrammes = async () => {
      if (isLoadingProfile) return;
      if (!userProfile) {
        setIsLoadingProgrammes(false);
        return;
      }

      try {
        const availableProgrammes = await api.getAvailableProgrammes(userProfile.id);
        setProgrammes(availableProgrammes);
      } catch (err) {
        console.error("Failed to fetch programmes:", err);
        setError("Failed to load available programmes");
      } finally {
        setIsLoadingProgrammes(false);
      }
    };

    fetchProgrammes();
  }, [isLoadingProfile, userProfile]);

  const handleJoinProgramme = useCallback(
    async (programme: Programme, { namesSaved = false }: { namesSaved?: boolean } = {}) => {
      if (!userProfile) {
        setError("User profile not found. Please refresh the page or contact support.");
        return;
      }
      if (programme.can_join === false) return;
      if (!namesSaved && needsNames(programme, userProfile)) {
        setFirstName(userProfile.first_name ?? "");
        setLastName(userProfile.last_name ?? "");
        setError(null);
        setNamePromptFor(programme.id);
        return;
      }

      setIsJoining(programme.id);
      setError(null);

      try {
        if (programme.price > 0) {
          const { session_url } = await api.createCheckoutSession(programme.id);
          window.location.href = session_url;
        } else {
          await api.joinProgramme(programme.id, userProfile.id);
          await refetchEnrollment();
          navigate("/");
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to join programme";
        setError(message);
        setIsJoining(null);
      }
    },
    [userProfile, refetchEnrollment, navigate]
  );

  // Same write order as the account page's NameRow: Clerk is the source of
  // truth, then mirror into our profile so the join sees the names at once.
  const handleSaveNamesAndJoin = async (programme: Programme) => {
    const first = firstName.trim();
    const last = lastName.trim();
    if (!first || !last) {
      setError("Enter both a first and a last name.");
      return;
    }
    setIsJoining(programme.id);
    setError(null);
    try {
      await clerkUser?.update({ firstName: first, lastName: last });
      await api.updateMyIdentity({ first_name: first, last_name: last });
      await refetchProfile();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save your name.");
      setIsJoining(null);
      return;
    }
    setNamePromptFor(null);
    await handleJoinProgramme(programme, { namesSaved: true });
  };

  // Auto-trigger checkout when arriving with ?programme_id=... (e.g. from the
  // marketing site's "Enrol Now" buttons). Fires at most once per mount.
  useEffect(() => {
    if (autoJoinAttempted.current) return;
    if (isLoadingProfile || isLoadingProgrammes) return;
    if (!userProfile) return;

    const targetId = searchParams.get("programme_id");
    if (!targetId) return;
    if (!userProfile) return;

    autoJoinAttempted.current = true;

    // Clear the query param so reloads / back-from-Stripe don't re-fire.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("programme_id");
        return next;
      },
      { replace: true }
    );

    (async () => {
      // Resolved by id directly — not from `programmes` — so a private
      // (invite-only) cohort's share link still resolves without listing
      // the cohort in the browsable list below (KC-389).
      const target =
        programmes.find((p) => p.id === targetId) ??
        (await api.getProgrammeById(targetId, userProfile.id));

      if (!target) {
        setError("That programme isn't currently available.");
        return;
      }
      if (target.can_join === false) {
        // Already enrolled — silently no-op; the existing card shows the state.
        return;
      }
      if (target.seats_limit != null && (target.seats_remaining ?? 0) === 0) {
        setError("That cohort is fully enrolled.");
        return;
      }

      handleJoinProgramme(target);
    })();
  }, [
    isLoadingProfile,
    isLoadingProgrammes,
    userProfile,
    programmes,
    searchParams,
    setSearchParams,
    handleJoinProgramme,
  ]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (isLoadingProfile || isLoadingProgrammes) {
    return (
      <MinimalLayout>
        <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
          <Loader2 className="w-8 h-8 animate-spin text-content-muted" />
        </div>
      </MinimalLayout>
    );
  }

  const displayError = profileError || error;
  // Taster copy when the Cohort in focus (the linked one, or every one shown)
  // is freemium — a 1-week taster isn't the 4-week certified programme (KC-504).
  const focused = programmes.find((p) => p.id === (namePromptFor ?? linkedId.current));
  const taster = focused
    ? Boolean(focused.is_freemium)
    : programmes.length > 0 && programmes.every((p) => p.is_freemium);

  return (
    <MinimalLayout>
      <div className="max-w-5xl mx-auto py-8 px-4">
        <WelcomeBanner taster={taster} />

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-content-foreground mb-3 font-['PT_Serif']">
            Available Programmes
          </h1>
          <p className="text-content-muted text-lg">
            {taster
              ? "Join a free taster to try your first Dominion Work Packages"
              : "Choose a programme to begin your Dominion certification journey"}
          </p>
        </div>

        {/* Error Message */}
        {displayError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{displayError}</span>
          </div>
        )}

        {/* Programmes List */}
        {programmes.length > 0 ? (
          <div className="space-y-4">
            {programmes.map((programme) => {
              const isEnrolled = programme.can_join === false;
              return (
                <div
                  key={programme.id}
                  className={`bg-white border p-6 transition-all ${
                    isEnrolled
                      ? "border-brand/30 bg-brand/5"
                      : "border-content-border hover:border-content-muted"
                  }`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Programme Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-2xl font-bold text-content-foreground font-['PT_Serif']">
                          {programme.name}
                        </h2>
                        {programme.is_freemium && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-content-subtle text-content-foreground border border-content-border text-xs font-medium uppercase tracking-wider">
                            Freemium
                          </span>
                        )}
                        {isEnrolled && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand/10 text-brand-foreground border border-brand/20 text-xs font-medium uppercase tracking-wider">
                            <CheckCircle className="w-3 h-3" />
                            Enrolled
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-content-muted text-sm">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          <span>Starts: {formatDate(programme.start_date)}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          <span>Ends: {formatDate(programme.end_date)}</span>
                        </div>
                        {programme.seats_limit != null && (
                          <div className="flex items-center gap-1.5">
                            <Users className="w-4 h-4" />
                            <span>
                              {(programme.seats_remaining ?? 0) === 1
                                ? "1 seat left"
                                : `${programme.seats_remaining ?? 0} seats left`}
                            </span>
                          </div>
                        )}
                        <div>
                          <span className="font-medium text-content-foreground">
                            {programme.total_weeks} weeks
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-content-foreground">
                            {programme.price > 0 ? formatCurrency(programme.price) : "FREE"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Join Button or Enrolled / Full State */}
                    {isEnrolled ? (
                      <button
                        onClick={async () => {
                          await refetchEnrollment();
                          navigate("/");
                        }}
                        className="border border-content-border text-content-foreground px-6 py-3 font-semibold hover:bg-content-subtle transition-all flex items-center justify-center gap-2 whitespace-nowrap text-sm"
                      >
                        <span>Go to Dashboard</span>
                      </button>
                    ) : programme.seats_limit != null && (programme.seats_remaining ?? 0) === 0 ? (
                      <button
                        disabled
                        className="border border-content-border text-content-muted px-6 py-3 font-semibold cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap text-sm"
                      >
                        <span>Fully enrolled</span>
                      </button>
                    ) : (
                      <div className="flex flex-col items-stretch gap-1">
                        <button
                          onClick={() => handleJoinProgramme(programme)}
                          disabled={isJoining !== null}
                          className="bg-primary text-primary-foreground px-6 py-3 font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap text-sm"
                        >
                          {isJoining === programme.id ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              <span>{programme.price > 0 ? "Redirecting..." : "Joining..."}</span>
                            </>
                          ) : (
                            <span>
                              {programme.price > 0
                                ? `Join for ${formatCurrency(programme.price)}`
                                : "Join Free"}
                            </span>
                          )}
                        </button>
                        {offer && programme.price > 0 && (
                          <span className="text-center text-xs font-semibold text-brand-foreground">
                            {offer}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {namePromptFor === programme.id && (
                    <form
                      className="mt-4 border-t border-content-border pt-4"
                      onSubmit={(event) => {
                        event.preventDefault();
                        handleSaveNamesAndJoin(programme);
                      }}
                    >
                      <p className="mb-3 text-sm text-content-muted">
                        Add your name to join. It appears on your participation badge.
                      </p>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                        <label className="flex-1 text-xs font-medium uppercase tracking-wider text-content-muted">
                          First name
                          <input
                            className={`${NAME_FIELD_CLASS} mt-1 normal-case tracking-normal`}
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            autoComplete="given-name"
                          />
                        </label>
                        <label className="flex-1 text-xs font-medium uppercase tracking-wider text-content-muted">
                          Last name
                          <input
                            className={`${NAME_FIELD_CLASS} mt-1 normal-case tracking-normal`}
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            autoComplete="family-name"
                          />
                        </label>
                        <button
                          type="submit"
                          disabled={isJoining !== null}
                          className="bg-primary text-primary-foreground px-6 py-2.5 font-semibold hover:opacity-90 transition-all disabled:opacity-50 text-sm whitespace-nowrap"
                        >
                          Save and join
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Empty State */
          <div className="bg-content-subtle border border-dashed border-content-border p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white border border-content-border mb-6">
              <AlertCircle className="w-8 h-8 text-content-muted" />
            </div>
            <h2 className="text-2xl font-semibold text-content-foreground mb-3 font-['PT_Serif']">
              No Programmes Available
            </h2>
            <p className="text-content-muted max-w-md mx-auto">
              There are currently no programmes available for enrollment. Please check back later or
              contact your administrator for more information.
            </p>
          </div>
        )}

        {/* Programme Overview Info */}
        {programmes.length > 0 && (
          <div className="mt-8 bg-white border border-content-border p-6">
            <h3 className="text-xs font-medium uppercase tracking-wider text-content-muted mb-4">
              What to Expect
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {(taster ? TASTER_EXPECTATIONS : PAID_EXPECTATIONS).map(([value, label]) => (
                <div key={label} className="bg-content-subtle border border-content-border p-4">
                  <div className="text-2xl font-bold text-content-foreground mb-1">{value}</div>
                  <div className="text-xs text-content-muted uppercase tracking-wider">{label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <TestCardInfo />
    </MinimalLayout>
  );
}
